import React, { useState } from 'react';
import { Users, Building2, Plus, AlertTriangle } from 'lucide-react';
import WorkspaceHeader from '../../workspace/WorkspaceHeader';
import ContactList from './ContactList';
import OrganizationList from './OrganizationList';
import ContactForm from './ContactForm';
import OrganizationForm from './OrganizationForm';
import EditContactForm from './EditContactForm';
import EditOrganizationForm from './EditOrganizationForm';
import { useEntity } from '../../../hooks/useEntity';
import { Link } from 'react-router-dom';
import { getOrganizationProfile } from '../../../lib/supabase/organization';
import type { Contact, Organization } from '../../../types/contacts';

type View = 'contacts' | 'organizations';
type FormType = 'contact' | 'organization' | null;

export default function ContactManager() {
  const [currentView, setCurrentView] = useState<View>('contacts');
  const [showForm, setShowForm] = useState<FormType>(null);
  const [contactType, setContactType] = useState<'customer' | 'team' | 'supplier' | 'partner'>('customer');
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const [organizationToEdit, setOrganizationToEdit] = useState<Organization | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showProfileRequiredModal, setShowProfileRequiredModal] = useState(false);
  const { entity, loading: entityLoading, error: entityError } = useEntity();

  const handleEditContact = (contact: Contact) => {
    setContactToEdit(contact);
  };

  const handleEditOrganization = (organization: Organization) => {
    setOrganizationToEdit(organization);
  };

  const handleFormSuccess = () => {
    setShowForm(null);
    setContactToEdit(null);
    setOrganizationToEdit(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAddButtonClick = (type: 'customer' | 'team' | 'supplier' | 'partner' = 'customer') => {
    if (entity && entity.id) {
      setContactType(type);
      setShowForm(currentView === 'contacts' ? 'contact' : 'organization');
    } else {
      // If entity doesn't exist, show the profile required modal
      setShowProfileRequiredModal(true);
    }
  };

  if (entityLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!entity || !entity.id) {
    return (
      <div className="space-y-6">
        <WorkspaceHeader
          title="Contact Manager"
          icon={Users}
          description="Keep track of your customers, suppliers and team"
        />
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Company Profile Required</h3>
          <p className="text-yellow-700 mb-6">
            You need to set up your company profile in the Command Center before you can manage contacts.
          </p>
          <Link 
            to="/dashboard"
            state={{ section: 'operations', area: 'company' }}
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Set Up Company Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        title="Contact Manager"
        icon={Users}
        description="Keep track of your customers, suppliers and team"
      />

      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentView('contacts')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              currentView === 'contacts'
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Contacts</span>
          </button>
          <button
            onClick={() => setCurrentView('organizations')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              currentView === 'organizations'
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Building2 className="w-5 h-5" />
            <span>Organizations</span>
          </button>
        </div>
        
        {currentView === 'contacts' && (
          <div className="flex space-x-2">
            <button
              onClick={() => handleAddButtonClick('customer')}
              className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            >
              <Plus className="w-5 h-5" />
              <span>Add Contact</span>
            </button>
            <button
              onClick={() => handleAddButtonClick('team')}
              className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
            >
              <Plus className="w-5 h-5" />
              <span>Add Team Member</span>
            </button>
            <div className="relative group">
              <button
                className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-5 h-5" />
                <span>More</span>
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg hidden group-hover:block z-10">
                <div className="py-1">
                  <button onClick={() => handleAddButtonClick('supplier')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100">Add Supplier</button>
                  <button onClick={() => handleAddButtonClick('partner')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100">Add Partner</button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        
        {currentView === 'organizations' && (
          <button
            onClick={() => handleAddButtonClick('customer')}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          >
            <Plus className="w-5 h-5" />
            <span>Add Organization</span>
          </button>
        )}
      </div>

      {currentView === 'contacts' ? (
        <ContactList onEditContact={handleEditContact} refreshTrigger={refreshTrigger} />
      ) : (
        <OrganizationList onEditOrganization={handleEditOrganization} refreshTrigger={refreshTrigger} />
      )}

      {showForm === 'contact' && (
        <ContactForm
          onClose={() => setShowForm(null)}
          defaultType={contactType}
          onSuccess={handleFormSuccess}
        />
      )}

      {showForm === 'organization' && (
        <OrganizationForm
          onClose={() => setShowForm(null)}
          onSuccess={handleFormSuccess}
        />
      )}
      
      {/* Company Profile Required Modal */}
      {showProfileRequiredModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Company Profile Required</h3>
              <p className="text-gray-600 mb-6">
                You need to set up your company profile before you can add contacts.
                Would you like to set up your company profile now?
              </p>
              <div className="flex justify-center space-x-4">
                <Link
                  to="/dashboard"
                  state={{ section: 'operations', area: 'company' }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                >
                  Set Up Company Profile
                </Link>
                <button
                  onClick={() => setShowProfileRequiredModal(false)}
                  className="text-gray-600 hover:text-gray-800 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {contactToEdit && (
        <EditContactForm
          contactId={contactToEdit.id}
          onClose={() => setContactToEdit(null)}
          onSuccess={handleFormSuccess}
        />
      )}

      {organizationToEdit && (
        <EditOrganizationForm
          organization={organizationToEdit}
          onClose={() => setOrganizationToEdit(null)}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}